import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Video, Eye, EyeOff, ExternalLink, Pencil, ArrowUp, ArrowDown, ImageOff, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { value: "numbers", label: "Numbers" },
  { value: "words", label: "Words" },
  { value: "names", label: "Names" },
  { value: "working_memory", label: "Working Memory" },
];

const SECTION_BADGE_CLASS = {  numbers: "bg-lime-500/15 text-lime-400 border-lime-500/30",
  words: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  names: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  working_memory: "bg-pink-500/15 text-pink-400 border-pink-500/30",
};

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    section: "numbers",
    title: "",
    description: "",
    vimeo_url: "",
  });

  const fetchVideos = async () => {
    try {
      const res = await authFetch(`${API}/admin/videos`);
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
        setCounts(data.section_counts || {});
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    try {
      const res = await authFetch(`${API}/admin/videos/${editing.video_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          section: editing.section,
          title: editing.title,
          description: editing.description,
          vimeo_url: editing.vimeo_url,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to save");
      toast.success("Video updated");
      setEditing(null);
      fetchVideos();
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  };

  const move = async (video, direction) => {
    const res = await authFetch(`${API}/admin/videos/${video.video_id}/move`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ direction }),
    });
    if (res.ok) fetchVideos();
    else toast.error("Failed to reorder");
  };

  const refreshThumbnails = async () => {
    const res = await authFetch(`${API}/admin/videos/refresh-thumbnails`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast.success(`${data.fetched} thumbnails fetched, ${data.unavailable} unavailable`);
      fetchVideos();
    } else {
      toast.error("Failed to refresh thumbnails");
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.vimeo_url.trim()) {
      toast.error("Title and Vimeo URL are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/admin/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, is_published: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Failed to add video");
      }
      toast.success("Video added");
      setForm({ section: form.section, title: "", description: "", vimeo_url: "" });
      setShowForm(false);
      fetchVideos();
    } catch (err) {
      toast.error(err.message || "Failed to add video");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublished = async (video) => {
    try {
      const res = await authFetch(`${API}/admin/videos/${video.video_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_published: !video.is_published }),
      });
      if (!res.ok) throw new Error("Failed");
      fetchVideos();
    } catch (e) {
      toast.error("Failed to update video");
    }
  };

  const deleteVideo = async (video) => {
    if (!window.confirm(`Delete "${video.title}"?`)) return;
    try {
      const res = await authFetch(`${API}/admin/videos/${video.video_id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Video deleted");
      fetchVideos();
    } catch (e) {
      toast.error("Failed to delete video");
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="admin-videos-section">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" />
          Tutorial Videos
          <span className="text-sm text-muted-foreground font-normal">
            ({videos.length})
          </span>
        </CardTitle>
        <Button
          size="sm"
          onClick={() => setShowForm(!showForm)}
          data-testid="admin-videos-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm ? "Cancel" : "Add video"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-videos-section-counts">
          {SECTIONS.map((s) => {
            const c = counts[s.value] || { total: 0, published: 0 };
            return (
              <span
                key={s.value}
                className={`text-xs px-2 py-1 rounded-sm border ${SECTION_BADGE_CLASS[s.value]}`}
                data-testid={`admin-videos-count-${s.value}`}
              >
                {s.label}: {c.published} live / {c.total}
              </span>
            );
          })}
          <Button
            size="sm"
            variant="outline"
            onClick={refreshThumbnails}
            data-testid="admin-videos-refresh-thumbs-btn"
          >
            <RefreshCw className="w-3 h-3 mr-2" />
            Refresh thumbnails
          </Button>
        </div>
        {showForm && (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-4 p-4 bg-background border border-border rounded-md"
            data-testid="admin-videos-form"
          >
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Section</Label>
              <select
                value={form.section}
                onChange={(e) => setForm({ ...form, section: e.target.value })}
                className="w-full bg-card border border-border rounded-md px-3 py-2 text-foreground"
                data-testid="admin-videos-section-select"
              >
                {SECTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Getting started with number memorisation"
                data-testid="admin-videos-title-input"
              />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">
                Vimeo URL
              </Label>
              <Input
                value={form.vimeo_url}
                onChange={(e) => setForm({ ...form, vimeo_url: e.target.value })}
                placeholder="https://vimeo.com/123456789"
                data-testid="admin-videos-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Public, unlisted (with hash), or player.vimeo.com URLs all work.
              </p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">
                Description (optional)
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary shown next to the featured video"
                rows={3}
                data-testid="admin-videos-description-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
              data-testid="admin-videos-submit-btn"
            >
              {submitting ? "Adding…" : "Add video"}
            </Button>
          </motion.form>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : videos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No tutorial videos yet. Add the first one to start showing it on the
            training pages.
          </p>
        ) : (
          <div className="space-y-6" data-testid="admin-videos-list">
            {SECTIONS.filter((s) => videos.some((v) => v.section === s.value)).map((s) => (
              <div key={s.value} className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {s.label} — trainees see this order
                </p>
                {videos
                  .filter((v) => v.section === s.value)
                  .map((v) => (
              <div
                key={v.video_id}
                className="flex items-start gap-3 p-3 bg-background border border-border rounded-md"
                data-testid={`admin-video-row-${v.video_id}`}
              >
                {v.thumbnail_url ? (
                  <img
                    src={v.thumbnail_url}
                    alt=""
                    className="w-24 h-14 object-cover rounded border border-border flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-24 h-14 rounded border border-destructive/50 bg-destructive/10 flex flex-col items-center justify-center flex-shrink-0 text-[9px] text-muted-foreground text-center px-1"
                    title="Vimeo did not return a thumbnail for this video"
                    data-testid={`admin-video-nothumb-${v.video_id}`}
                  >
                    <ImageOff className="w-4 h-4 mb-0.5 text-destructive" />
                    No thumbnail
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                        SECTION_BADGE_CLASS[v.section] || ""
                      }`}
                    >
                      {SECTIONS.find((s) => s.value === v.section)?.label || v.section}
                    </span>
                    {v.is_published ? (
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-primary/15 text-primary border border-primary/40"
                        data-testid={`admin-video-state-${v.video_id}`}
                      >
                        Published
                      </span>
                    ) : (
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-destructive/15 text-destructive border border-destructive/40"
                        data-testid={`admin-video-state-${v.video_id}`}
                      >
                        Draft — hidden from trainees
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {v.title}
                  </h4>
                  {v.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {v.description}
                    </p>
                  )}
                  <a
                    href={v.vimeo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline"
                  >
                    {v.vimeo_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>

                  {editing?.video_id === v.video_id && (
                    <div
                      className="mt-3 space-y-2 p-3 bg-card border border-border rounded-md"
                      data-testid={`admin-video-edit-form-${v.video_id}`}
                    >
                      <select
                        value={editing.section}
                        onChange={(e) => setEditing({ ...editing, section: e.target.value })}
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground"
                        data-testid={`admin-video-edit-section-${v.video_id}`}
                      >
                        {SECTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <Input
                        value={editing.title}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                        placeholder="Title"
                        data-testid={`admin-video-edit-title-${v.video_id}`}
                      />
                      <Input
                        value={editing.vimeo_url}
                        onChange={(e) => setEditing({ ...editing, vimeo_url: e.target.value })}
                        placeholder="Vimeo URL"
                        data-testid={`admin-video-edit-url-${v.video_id}`}
                      />
                      <Textarea
                        value={editing.description}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        placeholder="Description"
                        rows={2}
                        data-testid={`admin-video-edit-description-${v.video_id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit} data-testid={`admin-video-edit-save-${v.video_id}`}>
                          Save changes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(null)}
                          data-testid={`admin-video-edit-cancel-${v.video_id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => move(v, "up")}
                    title="Move up in this section"
                    data-testid={`admin-video-up-${v.video_id}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => move(v, "down")}
                    title="Move down in this section"
                    data-testid={`admin-video-down-${v.video_id}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setEditing(
                        editing?.video_id === v.video_id
                          ? null
                          : {
                              video_id: v.video_id,
                              section: v.section,
                              title: v.title,
                              description: v.description || "",
                              vimeo_url: v.vimeo_url,
                            }
                      )
                    }
                    title="Edit"
                    data-testid={`admin-video-edit-${v.video_id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => togglePublished(v)}
                    title={v.is_published ? "Unpublish" : "Publish"}
                    data-testid={`admin-video-toggle-${v.video_id}`}
                  >
                    {v.is_published ? (
                      <Eye className="w-4 h-4 text-primary" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteVideo(v)}
                    title="Delete"
                    data-testid={`admin-video-delete-${v.video_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminVideos;
